import { useState, useCallback, useEffect } from 'react';
import { AISettingsData, loadAISettings, saveAISettings, resetAISettings } from '../../utils/aiSettingsStorage';

export interface AISettingsState {
  aiSettings: AISettingsData;
}

export interface AISettingsActions {
  handleSettingsChange: (settings: AISettingsData) => void;
  handleResetSettings: () => void;
  loadSettings: () => void;
}

export interface UseAISettingsReturn extends AISettingsState, AISettingsActions {}

/**
 * Hook untuk menangani AI Settings dengan isolasi penuh
 * Sesuai dengan todo: "Refactor AI settings ke modul terpisah (useAISettings.ts) untuk isolasi fitur"
 * 
 * Tanggung jawab:
 * - AI Settings state management
 * - Session storage persistence
 * - Settings validation dan reset
 * - Load/save operations
 * 
 * TIDAK menangani:
 * - File validation (handled by useUploadValidation)
 * - Upload logic (handled by useFileProcessing)
 * - UI states seperti drag/drop (handled by useFileProcessing)
 */
export const useAISettings = (): UseAISettingsReturn => {
  // AI Settings state - HANYA untuk AI settings logic
  const [aiSettings, setAiSettings] = useState<AISettingsData>(loadAISettings());

  // Load AI settings from session storage on mount
  useEffect(() => {
    try {
      const savedSettings = loadAISettings();
      setAiSettings(savedSettings);
    } catch (error) {
      console.error('Error loading AI settings:', error);
      // Fallback to default settings
      const defaultSettings = resetAISettings();
      setAiSettings(defaultSettings);
    }
  }, []);

  // Load settings manually (for refresh scenarios)
  const loadSettings = useCallback(() => {
    try {
      const savedSettings = loadAISettings();
      setAiSettings(savedSettings);
    } catch (error) {
      console.error('Error loading AI settings:', error);
      // Fallback to current state, don't reset automatically
    }
  }, []);

  // Handle settings change with persistence
  const handleSettingsChange = useCallback((settings: AISettingsData) => {
    try {
      // Validate settings before saving
      if (!settings || typeof settings !== 'object') {
        throw new Error('Invalid settings object');
      }

      // Validate required fields
      if (typeof settings.title_length !== 'number' || 
          typeof settings.description_length !== 'number' || 
          typeof settings.keywords_count !== 'number') {
        throw new Error('Invalid settings values');
      }

      // Validate ranges
      if (settings.title_length < 30 || settings.title_length > 150) {
        throw new Error('Title length must be between 30-150 characters');
      }

      if (settings.description_length < 50 || settings.description_length > 300) {
        throw new Error('Description length must be between 50-300 characters');
      }

      if (settings.keywords_count < 10 || settings.keywords_count > 100) {
        throw new Error('Keywords count must be between 10-100');
      }

      // Update state and save to storage
      setAiSettings(settings);
      saveAISettings(settings);
    } catch (error) {
      console.error('Error saving AI settings:', error);
      // Don't update state if validation fails
      throw error; // Re-throw to let caller handle the error
    }
  }, []);

  // Reset settings to default
  const handleResetSettings = useCallback(() => {
    try {
      const defaultSettings = resetAISettings();
      setAiSettings(defaultSettings);
    } catch (error) {
      console.error('Error resetting AI settings:', error);
      // Fallback: manually create default settings
      const fallbackSettings: AISettingsData = {
        title_length: 80,
        description_length: 150,
        keywords_count: 50,
        custom_instruction: ''
      };
      setAiSettings(fallbackSettings);
      try {
        saveAISettings(fallbackSettings);
      } catch (saveError) {
        console.error('Error saving fallback settings:', saveError);
      }
    }
  }, []);

  return {
    // State
    aiSettings,
    
    // Actions
    handleSettingsChange,
    handleResetSettings,
    loadSettings
  };
};